/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CallerIdLookupGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { Search } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select-shadcn';
import type { LookupSourceFormState } from '../types';

interface CallerIdLookupGeneralSectionProps {
    formData: LookupSourceFormState;
    errors: Record<string, string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFieldChange: (field: string, value: any) => void;
    t: (key: string) => string;
}

export function CallerIdLookupGeneralSection({
    formData, errors, onFieldChange, t,
}: CallerIdLookupGeneralSectionProps) {
    const showUrl = ['http', 'ldap', 'database', 'opencnam'].includes(formData.sourceType);
    const showHttpMethod = formData.sourceType === 'http';
    const showAuth = ['http', 'ldap', 'database'].includes(formData.sourceType);
    const showToken = formData.sourceType === 'opencnam';

    return (
        <FormBox title={t('callerIdLookupSources.tabs.general')} icon={<Search className="h-5 w-5" />}>
            <div className="space-y-6">

                {/* Name + Source Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                    <Label htmlFor="name">
                        <SimpleTooltip content={t('callerIdLookupSources.fields.name.tooltip')}>
                            <span>
                                {t('callerIdLookupSources.fields.name.label')}
                                <span className="text-red-500 ml-1">*</span>
                            </span>
                        </SimpleTooltip>
                    </Label>
                    <div>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => onFieldChange('name', e.target.value)}
                            placeholder={t('callerIdLookupSources.fields.name.placeholder')}
                            className={errors.name ? 'border-red-500' : ''}
                        />
                        {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
                    </div>

                    <Label htmlFor="sourceType">
                        <SimpleTooltip content={t('callerIdLookupSources.fields.sourceType.tooltip')}>
                            <span>
                                {t('callerIdLookupSources.fields.sourceType.label')}
                                <span className="text-red-500 ml-1">*</span>
                            </span>
                        </SimpleTooltip>
                    </Label>
                    <div>
                        <Select value={formData.sourceType} onValueChange={(v) => onFieldChange('sourceType', v)}>
                            <SelectTrigger className={errors.sourceType ? 'border-red-500' : ''}>
                                <SelectValue placeholder={t('callerIdLookupSources.fields.sourceType.placeholder')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="internal">{t('callerIdLookupSources.sourceTypes.internal')}</SelectItem>
                                <SelectItem value="http">{t('callerIdLookupSources.sourceTypes.http')}</SelectItem>
                                <SelectItem value="ldap">{t('callerIdLookupSources.sourceTypes.ldap')}</SelectItem>
                                <SelectItem value="database">{t('callerIdLookupSources.sourceTypes.database')}</SelectItem>
                                <SelectItem value="opencnam">{t('callerIdLookupSources.sourceTypes.opencnam')}</SelectItem>
                                <SelectItem value="custom">{t('callerIdLookupSources.sourceTypes.custom')}</SelectItem>
                            </SelectContent>
                        </Select>
                        {errors.sourceType && <p className="text-sm text-red-500 mt-1">{errors.sourceType}</p>}
                    </div>
                </div>

                {/* URL (conditional) */}
                {showUrl && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                        <Label htmlFor="url">
                            <SimpleTooltip content={t('callerIdLookupSources.fields.url.tooltip')}>
                                <span>
                                    {t('callerIdLookupSources.fields.url.label')}
                                    <span className="text-red-500 ml-1">*</span>
                                </span>
                            </SimpleTooltip>
                        </Label>
                        <div className="col-span-3">
                            <Input
                                id="url"
                                value={formData.url}
                                onChange={(e) => onFieldChange('url', e.target.value)}
                                placeholder={t('callerIdLookupSources.fields.url.placeholder')}
                                className={errors.url ? 'border-red-500' : ''}
                            />
                            {errors.url && <p className="text-sm text-red-500 mt-1">{errors.url}</p>}
                        </div>
                    </div>
                )}

                {/* HTTP Method (conditional) */}
                {showHttpMethod && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                        <Label htmlFor="httpMethod">
                            <SimpleTooltip content={t('callerIdLookupSources.fields.httpMethod.tooltip')}>
                                {t('callerIdLookupSources.fields.httpMethod.label')}
                            </SimpleTooltip>
                        </Label>
                        <div>
                            <Select value={formData.httpMethod} onValueChange={(v) => onFieldChange('httpMethod', v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="GET">GET</SelectItem>
                                    <SelectItem value="POST">POST</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                {/* Auth (conditional) */}
                {showAuth && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                        <Label htmlFor="authUsername">
                            <SimpleTooltip content={t('callerIdLookupSources.fields.authUsername.tooltip')}>
                                {t('callerIdLookupSources.fields.authUsername.label')}
                            </SimpleTooltip>
                        </Label>
                        <div>
                            <Input
                                id="authUsername"
                                value={formData.authUsername}
                                onChange={(e) => onFieldChange('authUsername', e.target.value)}
                                placeholder={t('callerIdLookupSources.fields.authUsername.placeholder')}
                            />
                        </div>
                        <Label htmlFor="authPassword">
                            <SimpleTooltip content={t('callerIdLookupSources.fields.authPassword.tooltip')}>
                                {t('callerIdLookupSources.fields.authPassword.label')}
                            </SimpleTooltip>
                        </Label>
                        <div>
                            <Input
                                id="authPassword"
                                type="password"
                                value={formData.authPassword}
                                onChange={(e) => onFieldChange('authPassword', e.target.value)}
                                placeholder={t('callerIdLookupSources.fields.authPassword.placeholder')}
                            />
                        </div>
                    </div>
                )}

                {/* Token (conditional) */}
                {showToken && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                        <Label htmlFor="token">
                            <SimpleTooltip content={t('callerIdLookupSources.fields.token.tooltip')}>
                                {t('callerIdLookupSources.fields.token.label')}
                            </SimpleTooltip>
                        </Label>
                        <div className="col-span-3">
                            <Input
                                id="token"
                                type="password"
                                value={formData.token}
                                onChange={(e) => onFieldChange('token', e.target.value)}
                                placeholder={t('callerIdLookupSources.fields.token.placeholder')}
                            />
                        </div>
                    </div>
                )}

                {/* Timeout + Cache TTL */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                    <Label htmlFor="timeout">
                        <SimpleTooltip content={t('callerIdLookupSources.fields.timeout.tooltip')}>
                            {t('callerIdLookupSources.fields.timeout.label')}
                        </SimpleTooltip>
                    </Label>
                    <div>
                        <Input
                            id="timeout"
                            type="number"
                            min="1"
                            max="30"
                            value={formData.timeout}
                            onChange={(e) => onFieldChange('timeout', parseInt(e.target.value) || 5)}
                            className={errors.timeout ? 'border-red-500' : ''}
                        />
                        {errors.timeout && <p className="text-sm text-red-500 mt-1">{errors.timeout}</p>}
                    </div>

                    <Label htmlFor="cacheTtl">
                        <SimpleTooltip content={t('callerIdLookupSources.fields.cacheTtl.tooltip')}>
                            {t('callerIdLookupSources.fields.cacheTtl.label')}
                        </SimpleTooltip>
                    </Label>
                    <div>
                        <Input
                            id="cacheTtl"
                            type="number"
                            min="0"
                            max="3600"
                            value={formData.cacheTtl}
                            onChange={(e) => onFieldChange('cacheTtl', parseInt(e.target.value) || 300)}
                            className={errors.cacheTtl ? 'border-red-500' : ''}
                        />
                        {errors.cacheTtl && <p className="text-sm text-red-500 mt-1">{errors.cacheTtl}</p>}
                    </div>
                </div>

                {/* Enabled */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                    <Label htmlFor="enabled">
                        <SimpleTooltip content={t('callerIdLookupSources.fields.enabled.tooltip')}>
                            {t('callerIdLookupSources.fields.enabled.label')}
                        </SimpleTooltip>
                    </Label>
                    <div>
                        <Toggle
                            id="enabled"
                            value={formData.enabled}
                            onChange={(v) => onFieldChange('enabled', v)}
                        />
                    </div>
                </div>

            </div>
        </FormBox>
    );
}
